import React, { useState } from "react";

import { Alert } from "antd";
import moment from "moment";

import { Property } from "manager/interfaces/api";
import { DatePicker, TextArea } from "shared/components/Form";
import { UnderlineLink } from "shared/components/Links";
import { Modal } from "shared/components/Modal";
import useModal from "shared/hooks/useModal";

import {
  BILLING_STATUS,
  GO_LIVE_AFTER_STOP_HINT,
  GO_LIVE_EXACT_NOW_HINT,
  GO_LIVE_EXACT_NOW_TITLE,
  goLiveApplicantCopy,
  goLiveBillingCopy,
  goLiveSlackHint,
  goLiveSubmitLabel,
  isDateOnOrBeforeToday,
  partitionSetLiveSelection,
  shouldShowGoLiveExactNowHint,
  propertyUnitCount,
  todayDateInput,
  toBillingWriteAt,
} from "./billingDisplay";
import {
  DateActionRow,
  DateField,
  DateFieldLabel,
  DatePickerWrap,
  DateShortcuts,
  ImmediatePill,
  ImmediateTip,
  ImmediateWrap,
  ImpactCopy,
  ImpactHint,
  ImpactLabel,
  ImpactRow,
  ImpactTable,
  ImpactTitle,
  PropertySummary,
  PropertySummaryName,
  PropertySummaryUnits,
  RestartHint,
  SetLiveBody,
} from "./styled";

interface SetLiveModalProps {
  properties: Property[];
  submit: (payload: {
    billingLiveAt: string;
    reason?: string;
    goNow?: boolean;
  }) => Promise<void> | void;
  submitting?: boolean;
}

const IMMEDIATE_TIP =
  "Saves the property and changelog now with an exact go-live (not approximate). The go-live date stays the one you picked. The row still shows as scheduled until that day.";

const SetLiveModal = ({
  properties,
  submit,
  submitting = false,
}: SetLiveModalProps) => {
  const { closeActiveModalDialog } = useModal();
  const [date, setDate] = useState(todayDateInput());
  const [goNow, setGoNow] = useState(false);
  const [tipOn, setTipOn] = useState(false);
  const [reason, setReason] = useState("");
  const { eligible, skipped } = partitionSetLiveSelection(properties);
  const restarting = eligible.some(
    (property) => property.billingStatus === BILLING_STATUS.notLive
  );
  const dateOk = Boolean(date) && (!restarting || isDateOnOrBeforeToday(date));
  const reasonOk = !restarting || Boolean(reason.trim());
  const canSubmit = dateOk && reasonOk;
  const title =
    eligible.length === 1
      ? `Set ${eligible[0].name} live?`
      : `Set ${eligible.length} properties live?`;
  const skipHint = skipped.length
    ? ` Skipped ${skipped.length}: ${skipped
        .map((property) => property.name)
        .join(", ")}.`
    : " Other statuses are skipped.";
  const intro =
    eligible.length === 1 && !skipped.length
      ? "Billing starts on the go-live date. Finance will be notified in Slack."
      : `One date for selected Onboarding rows.${skipHint} Finance will be notified in Slack.`;
  const boxLabel =
    eligible.length === 1 ? eligible[0].name : `${eligible.length} properties`;
  const boxUnits =
    eligible.length === 1
      ? `${propertyUnitCount(eligible[0])} units`
      : "Onboarding only";

  const onSubmit = () => {
    if (!canSubmit) {
      return undefined;
    }
    return submit({
      billingLiveAt: toBillingWriteAt(date),
      goNow,
      ...(restarting ? { reason: reason.trim() } : {}),
    });
  };

  return (
    <Modal
      title={title}
      subtitle={intro}
      submit={onSubmit}
      submitButtonLabel={goLiveSubmitLabel(date, goNow, eligible.length)}
      // eligible only — mixed Onboarding+scheduled must not say "Set 2 live".
      submitButtonDisabled={!canSubmit}
      submitting={submitting}
      closeOnSubmit={false}
      afterClose={closeActiveModalDialog}
      showCancelLink
      width={560}
    >
      <Modal.Body>
        <SetLiveBody>
          <PropertySummary>
            <PropertySummaryName>{boxLabel}</PropertySummaryName>
            <PropertySummaryUnits>{boxUnits}</PropertySummaryUnits>
          </PropertySummary>
          <DateField>
            <DateFieldLabel>Go-live date</DateFieldLabel>
            <DateActionRow>
              <DatePickerWrap>
                <DatePicker
                  id="billingLiveAt"
                  name="billingLiveAt"
                  label=""
                  value={date}
                  onChange={setDate}
                  displayFormat="MM/DD/YYYY"
                  disabledDate={
                    restarting
                      ? (current) => current.isAfter(moment(), "day")
                      : undefined
                  }
                />
              </DatePickerWrap>
              <DateShortcuts>
                <UnderlineLink
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    setDate(todayDateInput());
                  }}
                >
                  Today
                </UnderlineLink>
                {!restarting && (
                  <ImmediateWrap
                    onMouseEnter={() => setTipOn(true)}
                    onMouseLeave={() => setTipOn(false)}
                  >
                    <ImmediatePill
                      type="button"
                      active={goNow}
                      onClick={() => setGoNow((current) => !current)}
                    >
                      Go live immediately
                    </ImmediatePill>
                    {tipOn && <ImmediateTip>{IMMEDIATE_TIP}</ImmediateTip>}
                  </ImmediateWrap>
                )}
              </DateShortcuts>
            </DateActionRow>
            {restarting && (
              <RestartHint>{GO_LIVE_AFTER_STOP_HINT}</RestartHint>
            )}
          </DateField>
          {/* Yellow box only after a past pick — today is the default date. */}
          {shouldShowGoLiveExactNowHint(date, goNow, restarting) && (
            <Alert
              type="warning"
              showIcon
              message={GO_LIVE_EXACT_NOW_TITLE}
              description={GO_LIVE_EXACT_NOW_HINT}
            />
          )}
          {restarting && (
            <TextArea
              id="reason"
              name="reason"
              label="Reason"
              required
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
          <ImpactTable>
            <ImpactRow>
              <ImpactLabel>Applicants</ImpactLabel>
              <ImpactCopy>
                <ImpactTitle>{goLiveApplicantCopy(date, goNow)}</ImpactTitle>
                <ImpactHint>
                  From the connected property management system.
                </ImpactHint>
              </ImpactCopy>
            </ImpactRow>
            <ImpactRow>
              <ImpactLabel>Billing</ImpactLabel>
              <ImpactCopy>
                <ImpactTitle>{goLiveBillingCopy(date, goNow)}</ImpactTitle>
                <ImpactHint>{goLiveSlackHint()}</ImpactHint>
              </ImpactCopy>
            </ImpactRow>
          </ImpactTable>
        </SetLiveBody>
      </Modal.Body>
    </Modal>
  );
};

export default SetLiveModal;
